import { KEYS } from '@/constants/key';

export function getToken() {
  return (
    (typeof localStorage !== 'undefined' && localStorage.getItem(KEYS.LOGIN_TOKEN_STORE_KEY)) ||
    undefined
  );
}

export function getRefreshToken() {
  return (
    (typeof localStorage !== 'undefined' && localStorage.getItem(KEYS.REFRESH_TOKEN_KEY)) ||
    undefined
  );
}

export function setToken(token: string) {
  if (typeof localStorage !== 'undefined' && token) {
    localStorage.setItem(KEYS.LOGIN_TOKEN_STORE_KEY, token);
  }
}

export function setRefreshToken(refreshToken: string) {
  if (typeof localStorage !== 'undefined' && refreshToken) {
    localStorage.setItem(KEYS.REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function removeToken() {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(KEYS.LOGIN_TOKEN_STORE_KEY);
  }
}

export function removeRefreshToken() {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(KEYS.REFRESH_TOKEN_KEY);
  }
}

export async function tick(delay: number = 0) {
  return new Promise<void>((res) => {
    setTimeout(res, delay);
  });
}

export function undefinedRefine(obj: Record<string, any>) {
  return JSON.parse(JSON.stringify(obj)); //remove key has undefined value
}

export function isJSON(str: string): boolean {
  try {
    return !!JSON.parse(str) && !!str;
  } catch (e) {
    return false;
  }
}

export function isEmpty(value: any) {
  return value === '' || value == null;
}

export function generateUrlParams(params: {
  [key: string]: string | number | (string | number)[] | undefined | boolean;
}): string {
  const urlParams = new URLSearchParams();

  for (const key in params) {
    const value = params[key];
    if (value === undefined || value === '') continue;

    if (Array.isArray(value)) {
      // Always send arrays as repeated params: key=value1&key=value2
      value.forEach((v) => {
        if (v !== undefined && v !== '') urlParams.append(key, String(v));
      });
    } else {
      urlParams.set(key, String(value));
    }
  }

  // Ensure spaces are encoded as %20 (not "+") to satisfy backend
  return urlParams.toString().replace(/\+/g, '%20');
}

export const dataURItoBlob = (dataURI: string): Blob => {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeString });
};

// Utility function to check if token is expired
export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    return true; // If we can't parse the token, consider it expired
  }
}

// Utility function to check if refresh token is expired
export function isRefreshTokenExpired(refreshToken: string): boolean {
  try {
    // Check if refresh token exists and is not empty
    if (!refreshToken || refreshToken.trim() === '') {
      return true;
    }

    // If refresh token is a JWT, check its expiration
    if (refreshToken.includes('.')) {
      const payload = JSON.parse(atob(refreshToken.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    }

    // For non-JWT refresh tokens, just check if they exist
    return false;
  } catch (error) {
    console.error('Error checking refresh token expiration:', error);
    return true;
  }
}

// Utility function to check if access token will expire soon (within 5 minutes)
export function isTokenExpiringSoon(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const fiveMinutes = 5 * 60; // 5 minutes in seconds
    return payload.exp - currentTime < fiveMinutes;
  } catch (error) {
    return true; // If we can't parse the token, consider it expiring soon
  }
}
